import { useState, useEffect } from 'react';

// Breakpoint definitions (following Tailwind CSS conventions)
const BREAKPOINTS = {
  sm: 640,   // Small devices (phones)
  md: 768,   // Medium devices (tablets)
  lg: 1024,  // Large devices (laptops)
  xl: 1280,  // Extra large devices (desktops)
  '2xl': 1536 // 2X Large devices (large desktops)
};

/**
 * Hook to detect current screen size and device type
 * @returns {Object} Screen size information and device type booleans
 */
export const useMobile = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = screenSize.width < BREAKPOINTS.sm;
  const isTablet = screenSize.width >= BREAKPOINTS.sm && screenSize.width < BREAKPOINTS.lg;
  const isDesktop = screenSize.width >= BREAKPOINTS.lg;
  const isSmallMobile = screenSize.width < 375; // iPhone SE and smaller
  const isLargeMobile = screenSize.width >= 375 && screenSize.width < BREAKPOINTS.sm;

  // Touch device detection
  const isTouchDevice = typeof window !== 'undefined' && 
    ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  // Orientation detection
  const isPortrait = screenSize.height > screenSize.width;
  const isLandscape = screenSize.width > screenSize.height;

  return {
    // Screen dimensions
    width: screenSize.width,
    height: screenSize.height,
    
    // Device type flags
    isMobile,
    isTablet, 
    isDesktop,
    isSmallMobile,
    isLargeMobile,
    isTouchDevice,
    
    // Orientation flags
    isPortrait,
    isLandscape,
    
    // Breakpoint helpers
    breakpoint: {
      isXs: screenSize.width < BREAKPOINTS.sm,
      isSm: screenSize.width >= BREAKPOINTS.sm && screenSize.width < BREAKPOINTS.md,
      isMd: screenSize.width >= BREAKPOINTS.md && screenSize.width < BREAKPOINTS.lg,
      isLg: screenSize.width >= BREAKPOINTS.lg && screenSize.width < BREAKPOINTS.xl,
      isXl: screenSize.width >= BREAKPOINTS.xl && screenSize.width < BREAKPOINTS['2xl'],
      is2xl: screenSize.width >= BREAKPOINTS['2xl']
    },
    
    // Utility functions
    isAbove: (breakpoint) => screenSize.width >= BREAKPOINTS[breakpoint],
    isBelow: (breakpoint) => screenSize.width < BREAKPOINTS[breakpoint],
    isBetween: (min, max) => screenSize.width >= BREAKPOINTS[min] && screenSize.width < BREAKPOINTS[max]
  };
};

/**
 * Hook for responsive values that change based on screen size
 * @param {Object} values - Object with breakpoint keys and corresponding values
 * @returns {*} The value for the current screen size
 */
export const useResponsiveValue = (values) => {
  const { breakpoint } = useMobile();
  
  // Priority order: 2xl -> xl -> lg -> md -> sm -> xs -> default
  if (values['2xl'] && breakpoint.is2xl) return values['2xl'];
  if (values.xl && breakpoint.isXl) return values.xl;
  if (values.lg && breakpoint.isLg) return values.lg;
  if (values.md && breakpoint.isMd) return values.md;
  if (values.sm && breakpoint.isSm) return values.sm;
  if (values.xs && breakpoint.isXs) return values.xs;
  
  // Fall back to default or first available value
  return values.default || values[Object.keys(values)[0]];
};

/**
 * Hook for conditional rendering based on screen size
 * @param {string} breakpoint - Minimum breakpoint to show content
 * @returns {boolean} Whether content should be shown
 */
export const useShowAbove = (breakpoint) => {
  const { isAbove } = useMobile();
  return isAbove(breakpoint);
};

/**
 * Hook for conditional rendering below a certain breakpoint
 * @param {string} breakpoint - Maximum breakpoint to show content
 * @returns {boolean} Whether content should be shown
 */
export const useShowBelow = (breakpoint) => {
  const { isBelow } = useMobile();
  return isBelow(breakpoint);
};

export default useMobile;